import {
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { ESettingKey } from '@mahawi/eshop-common/src/types';
import { Button, Divider, Popconfirm, Space, Tag } from 'antd';
import { type ColumnType } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import HolidayPeriodAdd from '../components/holiday-periods/holiday-period-add';
import Table from '../components/table';
import UpdatedAt from '../components/updated-at';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { settingsDelete, settingsLoadAll } from '../reducers/settings/reducer';
import { type ISettingRecord } from '../reducers/settings/types';
import { SPACING } from '../theme/tokens';

export interface DataType {
  key: string;
  id: number;
  startDate: string;
  endDate: string;
  shipResumeDate: string;
  createdAt: string;
}

export function getStatus(record: DataType): { label: string; color: string } {
  const today = dayjs();

  if (today.isAfter(dayjs(record.shipResumeDate), 'day')) {
    return { label: 'Past', color: 'default' };
  }

  if (!today.isBefore(dayjs(record.startDate), 'day')) {
    return { label: 'Active', color: 'green' };
  }

  return { label: 'Upcoming', color: 'blue' };
}

function SettingsHolidayPeriodsContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const holidayPeriods = useSelector(
    (s: RootState) => s.Settings.byKey[ESettingKey.HOLIDAY_PERIOD],
  );
  const settingsUpdatedAt = useSelector((s: RootState) => s.Settings.updatedAt);
  const settingsInProcess = useSelector((s: RootState) => s.Settings.inProcess);

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnType<object>[]>([]);
  const [editingHolidayPeriod, setEditingHolidayPeriod] =
    useState<ISettingRecord | null>(null);

  useEffect((): void => {
    const columnsUE: ColumnType<object>[] = [
      {
        title: 'Start date',
        dataIndex: 'startDate',
        sorter: (a: DataType, b: DataType): number =>
          dayjs(a.startDate).unix() - dayjs(b.startDate).unix(),
      },
      {
        title: 'End date',
        dataIndex: 'endDate',
      },
      {
        title: 'Shipping resumes',
        dataIndex: 'shipResumeDate',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        render: (_text: string, record: DataType): React.ReactElement => {
          const status = getStatus(record);
          return <Tag color={status.color}>{status.label}</Tag>;
        },
      },
      {
        title: 'Actions',
        dataIndex: 'actions',
        render: (_text: string, record: DataType): React.ReactElement => (
          <Space size={SPACING.sm}>
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={settingsInProcess}
              onClick={(): void => {
                setEditingHolidayPeriod({
                  id: record.id,
                  key: ESettingKey.HOLIDAY_PERIOD,
                  value: {
                    startDate: record.startDate,
                    endDate: record.endDate,
                    shipResumeDate: record.shipResumeDate,
                  },
                  createdAt: record.createdAt,
                });
              }}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete this holiday period?"
              onConfirm={(): void => {
                dispatch(settingsDelete(ESettingKey.HOLIDAY_PERIOD, record.id));
                if (editingHolidayPeriod?.id === record.id) {
                  setEditingHolidayPeriod(null);
                }
              }}
              disabled={settingsInProcess}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={settingsInProcess}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

    if (!holidayPeriods) {
      setDataSource([]);

      return;
    }

    const dataSourceUE: DataType[] = holidayPeriods
      .map((holidayPeriod: ISettingRecord): DataType => ({
        key: String(holidayPeriod.id),
        id: holidayPeriod.id,
        startDate: holidayPeriod.value.startDate,
        endDate: holidayPeriod.value.endDate,
        shipResumeDate: holidayPeriod.value.shipResumeDate,
        createdAt: holidayPeriod.createdAt,
      }))
      .sort(
        (a: DataType, b: DataType): number =>
          dayjs(a.startDate).unix() - dayjs(b.startDate).unix(),
      );

    setDataSource(dataSourceUE);
    setColumns(columnsUE);
  }, [
    holidayPeriods,
    settingsUpdatedAt,
    settingsInProcess,
    editingHolidayPeriod,
    dispatch,
  ]);

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader title="Holiday periods" />

      <Space
        size={SPACING.lg}
        align="center"
        separator={<Divider orientation="vertical" />}
      >
        <Button
          type={!holidayPeriods ? 'primary' : 'default'}
          onClick={(): void => {
            dispatch(settingsLoadAll(ESettingKey.HOLIDAY_PERIOD));
          }}
          disabled={settingsInProcess}
        >
          Reload all <ReloadOutlined />
        </Button>

        <UpdatedAt value={settingsUpdatedAt} />
      </Space>

      <HolidayPeriodAdd
        editingHolidayPeriod={editingHolidayPeriod}
        onEditSubmitted={(): void => setEditingHolidayPeriod(null)}
      />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={settingsInProcess && !holidayPeriods}
      />
    </Space>
  );
}

export default SettingsHolidayPeriodsContainer;
